import { Link } from "react-router-dom";
import { Clock, Facebook, Instagram, Mail, MapPin, Music2, Phone } from "lucide-react";
import { useShop } from "@/lib/shop";

const SOCIALS = [
  { key: "instagram", label: "Instagram", Icon: Instagram },
  { key: "facebook", label: "Facebook", Icon: Facebook },
  { key: "tiktok", label: "TikTok", Icon: Music2 },
];

const Footer = () => {
  const shop = useShop();

  return (
    <footer data-testid="main-footer" className="grain relative overflow-hidden bg-bark text-sand">
      <div className="relative z-[2] mx-auto grid max-w-7xl gap-14 px-6 py-24 md:grid-cols-3 lg:px-10">
        <div>
          <img src="/logo.png" alt="Sama Keur Shop — Votre maison, notre passion" className="w-44 rounded-xl" data-testid="footer-logo" />
          <p className="mt-4 max-w-xs text-sm leading-relaxed text-sand/60">
            « Sama keur » — ma maison, en wolof. Des pièces choisies avec soin pour les maisons du Sénégal, livrées partout à Dakar.
          </p>
          <div className="mt-6 flex gap-2">
            {["Wave", "Orange Money", "Visa", "Mastercard"].map((p) => (
              <span key={p} className="border border-sand/20 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-widest text-sand/60">
                {p}
              </span>
            ))}
          </div>
          {SOCIALS.some(({ key }) => shop[key]) && (
            <div className="mt-8">
              <p className="text-xs font-semibold uppercase tracking-[0.25em] text-ochre">Suivez-nous</p>
              <div className="mt-4 flex gap-3">
                {SOCIALS.filter(({ key }) => shop[key]).map(({ key, label, Icon }) => (
                  <a
                    key={key}
                    data-testid={`footer-social-${key}`}
                    href={shop[key]}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    className="flex h-10 w-10 items-center justify-center rounded-full border border-sand/20 text-sand/60 transition-colors duration-300 hover:border-ochre hover:text-ochre"
                  >
                    <Icon className="h-4 w-4" />
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-ochre">Navigation</p>
          <ul className="mt-6 space-y-3">
            {[
              { to: "/", label: "Accueil" },
              { to: "/boutique", label: "Boutique" },
              { to: "/suivi", label: "Suivi de commande" },
              { to: "/commander", label: "Finaliser ma commande" },
            ].map((l) => (
              <li key={l.to}>
                <Link to={l.to} data-testid={`footer-link-${l.label.toLowerCase().replace(/ /g, "-")}`} className="text-sm text-sand/70 transition-colors duration-300 hover:text-ochre">
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-ochre">Nous trouver</p>
          <ul className="mt-6 space-y-4 text-sm text-sand/70">
            <li className="flex items-start gap-3">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-ochre" />
              <span data-testid="footer-address">{shop.address}</span>
            </li>
            <li className="flex items-center gap-3">
              <Phone className="h-4 w-4 shrink-0 text-ochre" />
              <a data-testid="footer-phone" href={`tel:${shop.phone.replace(/\s/g, "")}`} className="transition-colors hover:text-ochre">{shop.phone}</a>
            </li>
            <li className="flex items-center gap-3">
              <Mail className="h-4 w-4 shrink-0 text-ochre" />
              <a data-testid="footer-email" href={`mailto:${shop.email}`} className="transition-colors hover:text-ochre">{shop.email}</a>
            </li>
            <li className="flex items-center gap-3">
              <Clock className="h-4 w-4 shrink-0 text-ochre" />
              <span data-testid="footer-hours">{shop.hours}</span>
            </li>
          </ul>
          <p className="mt-6 text-xs text-sand/40">Livraison en 48h sur Dakar et sa banlieue.</p>
        </div>
      </div>
      <div className="relative z-[2] overflow-hidden px-6">
        <p className="select-none text-center font-display text-[17vw] font-bold leading-[0.85] text-sand/[0.07]">SAMA-KEUR</p>
      </div>
      <div className="relative z-[2] border-t border-sand/10 px-6 py-6 text-center text-xs text-sand/40">
        © 2026 Sama-Keur — Dakar, Sénégal. Tous droits réservés.
      </div>
    </footer>
  );
};

export default Footer;
